import { createLogger } from '@metron/shared';

const logger = createLogger('ApiClient');

interface ApiClientConfig {
  getToken: () => Promise<string | null>;
  onUnauthorized: () => void;
}

interface RequestOptions extends RequestInit {
  baseUrl?: string;
}

export function createApiClient(config: ApiClientConfig) {
  const { getToken, onUnauthorized } = config;

  async function request<T>(
    url: string,
    options: RequestOptions = {},
  ): Promise<T> {
    // Get token - Amplify's fetchAuthSession handles refresh automatically
    const token = await getToken();

    if (!token) {
      logger.warn({ action: 'no_token' }, 'No authentication token available');
      throw new Error('No authentication token available');
    }

    const { baseUrl = '', ...fetchOptions } = options;
    const fullUrl = baseUrl + url;

    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...options.headers,
    };

    try {
      const response = await fetch(fullUrl, {
        ...fetchOptions,
        headers,
      });

      // Handle 401 responses as authentication failure
      if (response.status === 401) {
        logger.warn({
          status: response.status,
          url: fullUrl,
          action: 'unauthorized_response',
        }, 'Received 401 response');
        onUnauthorized();
        throw Object.assign(new Error('Unauthorized - please sign in again'), { status: 401 });
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        // Handle standardized error format { error: { code, message, ... } }
        const errorMessage = errorData.error?.message || errorData.message ||
          `Request failed with status ${response.status}`;
        const err = new Error(errorMessage);
        Object.assign(err, { status: response.status, body: errorData });
        throw err;
      }

      return await response.json();
    } catch (error) {
      // Re-throw the error after handling
      throw error;
    }
  }

  return {
    get: <T>(url: string, options?: RequestOptions) =>
      request<T>(url, { ...options, method: 'GET' }),
    post: <T>(url: string, body?: unknown, options?: RequestOptions) =>
      request<T>(url, {
        ...options,
        method: 'POST',
        body: body ? JSON.stringify(body) : undefined,
      }),
    put: <T>(url: string, body?: unknown, options?: RequestOptions) =>
      request<T>(url, {
        ...options,
        method: 'PUT',
        body: body ? JSON.stringify(body) : undefined,
      }),
    patch: <T>(url: string, body?: unknown, options?: RequestOptions) =>
      request<T>(url, {
        ...options,
        method: 'PATCH',
        body: body ? JSON.stringify(body) : undefined,
      }),
    delete: <T>(url: string, options?: RequestOptions) =>
      request<T>(url, { ...options, method: 'DELETE' }),
  };
}
